import L from 'leaflet'
import Utils from 'utils'

/**
 * Creates a Leaflet divIcon with a number (order) for a point.
 *
 * @param item - The item that contains the point data.
 * @param isInArea - A boolean indicating whether the item is within a specific area.
 * @param isSelected - An optional boolean indicating whether the item is selected.
 *
 * @returns A Leaflet divIcon with a white, rounded background and a centered number.
 */
const numberIcon = (item: any, isInArea: boolean, isSelected?: boolean) => {
  const icon = L.divIcon({
    className: `rounded-full !w-8 !h-8 flex ${
      item.order < 100 ? 'text-base' : 'text-sm'
    } text-white !-mt-[16px] !-ml-[16px] ${Utils.getPointColor(
      item,
      isInArea,
      isSelected
    )}`,
    html: `<div style="margin:auto;">${item.order ?? ''}</div>`
  })
  return icon
}

/**
 * A function to generate a small number icon for a point.
 *
 * @param {object} item - The item that contains the point data.
 * @param {string} [selectedId] - The id of the selected point.
 * @param {boolean} [isLarge] - Whether to generate a larger icon.
 * @param {boolean} [showNumbers] - Whether to show the numbers on the map.
 * @returns {L.DivIcon} A leaflet icon.
 */
const PointStyle = (
  item: any,
  selectedId?: string,
  isLarge?: boolean,
  showNumbers?: boolean
) => {
  const size = isLarge ? 30 : 15
  let fontSize = '5px'
  if (isLarge) {
    fontSize = item.order < 100 ? '12px' : '10px'
  } else {
    fontSize = item.order < 100 ? '6px' : '5px'
  }

  const isSelected = item.id === selectedId
  const textColor = '#fff'
  const fontWeight = '600' // font-medium

  let className = 'point-marker point-hover'
  if (isSelected) {
    className += ' selected-point'
  } else {
    className += ' unselected-normal'
  }

  return L.divIcon({
    className,
    iconAnchor: [size / 2, size / 2],
    html: `
      <div class="point-icon" style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        color: ${textColor};
        font-size: ${fontSize};
        font-weight: ${fontWeight};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.15);
        z-index: ${!selectedId ? 9998 : 9999};
        border: ${isLarge ? '2.5px solid white' : '1.5px solid white'};
        cursor: pointer;
      ">
        ${showNumbers ? item.order ?? '' : ''}
      </div>
    `
  })
}

/**
 * Creates a rotating marker icon with a specified rotation angle.
 *
 * @param {any} rotationAngle - The angle in degrees to rotate the marker icon.
 * @returns {L.DivIcon} A Leaflet DivIcon configured with the specified rotation.
 */

const rotatedMarkerIcon = (rotationAngle: any) => {
  const icon = new L.DivIcon({
    iconUrl: '/assets/view.png',
    iconAnchor: [35, 50],
    className: 'rotating-marker',
    html: `<img src="/assets/view.png" draggable="false" style="clip-path: polygon(0 0, 50% 100%, 100% 0); height:50px !important; width:70px !important;transform: rotate(${rotationAngle}deg);transform-origin: 50% 100%;" />`
  })
  return icon
}

/**
 * Creates a dense rotating marker icon with a specified rotation angle.
 *
 * @param {any} rotationAngle - The angle in degrees to rotate the marker icon.
 * @returns {L.DivIcon} A Leaflet DivIcon configured with the specified rotation and dense view.
 */

const rotatedMarkerIconDense = (rotationAngle: any) => {
  const icon = new L.DivIcon({
    iconUrl: '/assets/dense-view.png',
    iconAnchor: [35, 50],
    className: 'rotating-marker',
    html: `<img src="/assets/dense-view.png" draggable="false" style="clip-path: polygon(0 0, 50% 100%, 100% 0); height:50px !important; width:70px !important;transform: rotate(${rotationAngle}deg);transform-origin: 50% 100%;" />`
  })
  return icon
}

export { numberIcon, PointStyle, rotatedMarkerIcon, rotatedMarkerIconDense }
